/**
 * User ID debugging utilities for Keyword OS
 * 
 * These utilities help track and validate user ID usage throughout the application
 * to prevent Clerk user IDs from being passed to Supabase queries.
 */

import { debugLog, debugWarn, debugError } from '@/lib/debugUtils';

export interface UserIdDebugInfo {
  value: string;
  isClerkId: boolean;
  isValidUuid: boolean;
  source: string;
  timestamp: string;
}

/**
 * Validates if a user ID is a valid Supabase UUID
 */
export const isValidSupabaseUserId = (userId: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(userId);
};

/**
 * Checks if a user ID looks like a Clerk user ID
 */
export const isClerkUserId = (userId: string): boolean => {
  return userId.startsWith('user_');
};

/**
 * Creates debug information for a user ID
 */
export const createUserIdDebugInfo = (userId: string, source: string): UserIdDebugInfo => {
  return {
    value: userId,
    isClerkId: isClerkUserId(userId),
    isValidUuid: isValidSupabaseUserId(userId),
    source,
    timestamp: new Date().toISOString()
  };
};

/**
 * Logs user ID debug information to console (authorized users only)
 */
export const debugUserId = (userId: string, source: string, userEmail?: string | null): UserIdDebugInfo => {
  const debugInfo = createUserIdDebugInfo(userId, source);
  
  debugLog(userEmail, `[UserID Debug] ${source}`);
  debugLog(userEmail, 'User ID:', debugInfo.value);
  debugLog(userEmail, 'Is Clerk ID:', debugInfo.isClerkId);
  debugLog(userEmail, 'Is Valid UUID:', debugInfo.isValidUuid);
  debugLog(userEmail, 'Source:', debugInfo.source);
  debugLog(userEmail, 'Timestamp:', debugInfo.timestamp);
  
  if (debugInfo.isClerkId) {
    debugWarn(userEmail, '⚠️ WARNING: Clerk user ID detected! This should be mapped to a Supabase UUID.');
  }
  
  if (!debugInfo.isValidUuid && !debugInfo.isClerkId) {
    debugWarn(userEmail, '⚠️ WARNING: Invalid user ID format detected!');
  }
  
  return debugInfo;
};

/**
 * Validates user ID and throws error if invalid for database operations
 */
export const validateUserIdForDatabase = (userId: string, operation: string): void => {
  if (!userId) {
    throw new Error(`[${operation}] User ID is required for database operations`);
  }
  
  if (isClerkUserId(userId)) {
    throw new Error(`[${operation}] Clerk user ID detected: ${userId}. Expected Supabase UUID.`);
  }
  
  if (!isValidSupabaseUserId(userId)) {
    throw new Error(`[${operation}] Invalid UUID format: ${userId}`);
  }
};

/**
 * Safe user ID getter with validation and debugging
 */
export const getSafeUserId = (userId: string | null, source: string, userEmail?: string | null): string | null => {
  if (!userId) {
    debugUserId('null', source, userEmail);
    return null;
  }
  
  const debugInfo = debugUserId(userId, source, userEmail);
  
  // Return null if we detect a Clerk ID that shouldn't be used for database operations
  if (debugInfo.isClerkId) {
    debugError(userEmail, `[${source}] Clerk user ID detected - returning null to prevent database errors`);
    return null;
  }
  
  // Return null if invalid UUID format
  if (!debugInfo.isValidUuid) {
    debugError(userEmail, `[${source}] Invalid UUID format - returning null to prevent database errors`);
    return null;
  }
  
  return userId;
};
